import { Contact } from '@b2chatorg/chat-center-widget-sdk/dist/types';
import B2ChatClient from '@client-sdk';
import { activeChatSuccess } from '@src/reducers/chats';
import { chatByIdSelector } from '@src/selectors/chats';
import { BotAccount } from '@src/types';
import { ChatTray } from '@src/types/chats';
import { IntegrationApp } from '@src/types/integrations';
import { B2ChatAPI } from '@types-api';
import { eventChannel, SagaIterator } from 'redux-saga';
import { call, delay, put, select, take } from 'redux-saga/effects';

const hubspotListeners = {
  GET_AUTH_TOKEN: 'GET_AUTH_TOKEN',
  SEND_AUTH_TOKEN: 'SEND_AUTH_TOKEN',

  CREATE_CONTACT: 'CREATE_CONTACT',
  CREATE_CONTACT_FAILURE: 'CREATE_CONTACT_FAILURE',

  UPDATE_CONTACT: 'UPDATE_CONTACT',
  UPDATE_CONTACT_FAILURE: 'UPDATE_CONTACT_FAILURE',

  GET_CONTACTS: 'GET_CONTACTS',
  GET_CONTACTS_FAILURE: 'GET_CONTACTS_FAILURE',

  GET_WS_ACCOUNTS: 'GET_WS_ACCOUNTS',
  SEND_WS_ACCOUNTS: 'SEND_WS_ACCOUNTS',
  SEND_WS_ACCOUNTS_FAILURE: 'SEND_WS_ACCOUNTS_FAILURE',

  GET_WS_CONTACT_ACCOUNT: 'GET_WS_CONTACT_ACCOUNT',
  GET_WS_CONTACT_ACCOUNT_FAILURE: 'GET_WS_CONTACT_ACCOUNT_FAILURE',

  GET_WS_ACCOUNT_TEMPLATES: 'GET_WS_ACCOUNT_TEMPLATES',
  SEND_WS_ACCOUNT_TEMPLATES: 'SEND_WS_ACCOUNT_TEMPLATES',
  SEND_WS_ACCOUNT_TEMPLATES_FAILURE: 'SEND_WS_ACCOUNT_TEMPLATES_FAILURE',

  GENERATE_CONVERSATION: 'GENERATE_CONVERSATION',
};

type PostMessageToHubspot = <T extends Record<string, unknown>>(
  data: T
) => void;

function createHubspotMessageChannel() {
  return eventChannel<MessageEvent>(emit => {
    const handler = (event: MessageEvent) => {
      emit(event);
    };

    window.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
    };
  });
}

function* getWsAccountsSaga(postMessage: PostMessageToHubspot) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: B2ChatAPI.Response<BotAccount[]> = yield call(
      B2ChatClient.resources.wsAccounts.actions.getWsAccounts,
      {}
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.SEND_WS_ACCOUNTS,
      data: response.data,
    });
  } catch (error) {
    postMessage({ action: hubspotListeners.SEND_WS_ACCOUNTS_FAILURE });
  }
}

function* getWsContactAccountsSaga(
  contactId: string,
  postMessage: PostMessageToHubspot
) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: B2ChatAPI.Response<BotAccount[]> = yield call(
      B2ChatClient.resources.contacts.actions.getBotAccounts,
      { params: { id: contactId } }
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.GET_WS_CONTACT_ACCOUNT,
      data: response.data,
    });
  } catch (error) {
    postMessage({ action: hubspotListeners.GET_WS_CONTACT_ACCOUNT_FAILURE });
  }
}

function* getWsAccountTemplatesSaga(
  accountId: string,
  postMessage: PostMessageToHubspot
) {
  try {
    const response: B2ChatAPI.Response<IntegrationApp[]> = yield call(
      B2ChatClient.resources.wsTemplates.actions.getWsTemplates,
      {
        params: { whatsappAccountId: accountId },
      }
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.SEND_WS_ACCOUNT_TEMPLATES,
      data: response.data,
    });
  } catch (error) {
    postMessage({ action: hubspotListeners.SEND_WS_ACCOUNT_TEMPLATES_FAILURE });
  }
}

function* getContactsSaga(
  { mobileNumber, email }: { mobileNumber: string; email: string },
  postMessage: PostMessageToHubspot
) {
  try {
    const response: B2ChatAPI.Response<Contact[]> = yield call(
      B2ChatClient.resources.contacts.actions.v2FetchContact,
      {
        params: { email, mobileNumber },
      }
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.GET_CONTACTS,
      data: response.data,
    });
  } catch (error) {
    postMessage({ action: hubspotListeners.GET_CONTACTS_FAILURE });
  }
}

function* createContactSaga(
  contact: Contact,
  postMessage: PostMessageToHubspot
) {
  try {
    const response: B2ChatAPI.Response<Contact> = yield call(
      B2ChatClient.resources.contacts.actions.v2CreateContact,
      {
        data: contact,
      }
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.CREATE_CONTACT,
      data: response.data,
    });
  } catch (error) {
    postMessage({ action: hubspotListeners.CREATE_CONTACT_FAILURE });
  }
}

function* updateContactPhoneSaga(
  { contactId, mobileNumber }: { contactId: string; mobileNumber: string },
  postMessage: PostMessageToHubspot
) {
  try {
    const response: B2ChatAPI.Response<Contact[]> = yield call(
      B2ChatClient.resources.contacts.actions.v2UpdateContact,
      {
        params: { contactId },
        data: { mobileNumber },
      }
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.UPDATE_CONTACT,
      data: response.data,
    });
  } catch (error) {
    postMessage({ action: hubspotListeners.UPDATE_CONTACT_FAILURE });
  }
}

function* chatExistsInConsole(chatId: string, maxRetries = 15, interval = 600) {
  for (let i = 0; i < maxRetries; i += 1) {
    const chat: ChatTray = yield select(chatByIdSelector(chatId));
    if (chat?.id) return true;
    yield delay(interval);
  }
  return false;
}

function* createConversationSaga(
  {
    botId,
    provider,
    contactId,
    templateMsgId,
    wsAccountUsername,
    wsAccountIdentifier,
  }: {
    botId: number;
    provider: string;
    contactId: number;
    templateMsgId: string;
    wsAccountUsername: string;
    wsAccountIdentifier: string;
  },
  postMessage: PostMessageToHubspot
) {
  try {
    const response: B2ChatAPI.Response<{ chatId: string }> = yield call(
      B2ChatClient.resources.urlPreview.actions.start,
      {
        params: {
          'bot-id': botId,
          'contact-id': contactId,
          'contact-account': wsAccountIdentifier,
          'via-bot-account': wsAccountUsername,
          'via-msg-provider': provider,
          'templated-msg-id': templateMsgId,
        },
      }
    );

    if (response.error) throw response;

    postMessage({
      action: hubspotListeners.GENERATE_CONVERSATION,
      data: response.data,
      error: false,
    });

    const chatExists: boolean = yield chatExistsInConsole(response.data.chatId);

    if (chatExists) yield put(activeChatSuccess(response.data.chatId));
  } catch (error) {
    postMessage({
      action: hubspotListeners.GENERATE_CONVERSATION,
      data: [],
      error,
    });
  }
}

export default function* integrationHubspotSaga(): SagaIterator {
  const channel = yield call(createHubspotMessageChannel);

  while (true) {
    const event: MessageEvent = yield take(channel);
    const hubspotWindow = event.source as Window | null;
    //prettier-ignore
    const postMessageToHubspot: PostMessageToHubspot = <T extends Record<string, unknown>>(data: T) => {
      hubspotWindow?.postMessage(data, '*');
    };

    // eslint-disable-next-line default-case
    switch (event.data.action) {
      case hubspotListeners.GET_WS_ACCOUNTS:
        yield call(getWsAccountsSaga, postMessageToHubspot);
        break;
      case hubspotListeners.GET_WS_ACCOUNT_TEMPLATES:
        yield call(
          getWsAccountTemplatesSaga,
          event.data.data.accountId,
          postMessageToHubspot
        );
        break;

      case hubspotListeners.GET_WS_CONTACT_ACCOUNT:
        yield call(
          getWsContactAccountsSaga,
          event.data.data.contactId,
          postMessageToHubspot
        );
        break;

      case hubspotListeners.GENERATE_CONVERSATION:
        yield call(
          createConversationSaga,
          event.data.data,
          postMessageToHubspot
        );
        break;

      case hubspotListeners.GET_CONTACTS:
        yield call(getContactsSaga, event.data.data, postMessageToHubspot);
        break;

      case hubspotListeners.CREATE_CONTACT:
        yield call(createContactSaga, event.data.data, postMessageToHubspot);
        break;

      case hubspotListeners.UPDATE_CONTACT:
        yield call(
          updateContactPhoneSaga,
          event.data.data,
          postMessageToHubspot
        );
        break;
    }
  }
}
